/**
 * Org-session slice — display metadata for the currently active organization
 * (+ workspace scope) and the switch-org-session flow (POST /auth/sessions/org).
 *
 * Security model (designs/admin-mfa-org-sessions.md, invariant #2):
 * - The client-side `activeOrgId` / `activeOrgName` / `activeWorkspaceId`
 *   here are a UX hint ONLY. They are never sent as authorization material
 *   and are never persisted separately from the JWT — the server re-derives
 *   the org (+workspace) scope from the verified session on every request
 *   and re-authorizes on every refresh.
 * - A switch is atomic: old tokens/UI stay fully in place until the server
 *   returns a new token pair (2xx). Only then are both tokens adopted at once
 *   (lib/auth `adoptSession`) and the RTK Query cache reset so no stale or
 *   in-flight data from the previous org survives.
 * - Any non-2xx (401/403/404/5xx) leaves the previous session untouched.
 *
 * Org+workspace sessions (ADDITIVE): `POST /auth/sessions/org` accepts an
 * optional `workspaceId` the server verifies belongs to the target org before
 * minting the claim. The client resolves the scope with a convenience
 * cascade: remembered workspace (if still valid) → exactly-one-workspace
 * auto-pick → otherwise org-only (the WorkspaceSelector asks the user).
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '@/lib/api';
import {
  adoptSession,
  decodeJwtClaims,
  getRefreshToken,
  getToken,
  logoutOrgSessionFamily,
  switchOrgSession,
} from '@/lib/auth';
import { apiSlice } from '../api/apiSlice';
import { loginUser, logoutUser, resetAuth, sessionEstablished } from './authSlice';
import type { OrgSessionResponse } from '@/types';

/** Last org / workspace the user switched to, so a re-login lands back there. */
const LAST_ORG_KEY = 'cluster_last_org_id';
const LAST_WORKSPACE_KEY = 'cluster_last_workspace_id';

interface OrgLite {
  _id: string;
  name: string;
}
interface WorkspaceLite {
  id: string;
  name: string;
  kind?: string;
  status?: string;
}

/**
 * Pull the org + workspace hints out of the CURRENT access token's claims.
 * Decode is unverified by definition (the browser cannot check the RS256
 * signature) — the result is for DISPLAY only and must never drive an
 * authorization decision; the server re-derives the scope from the verified
 * session. Returns nulls for missing/invalid tokens.
 */
export function readOrgFromAccessToken(token: string | null): {
  orgId: string | null;
  organizationName: string | null;
  workspaceId: string | null;
} {
  const claims = decodeJwtClaims(token);
  if (!claims) return { orgId: null, organizationName: null, workspaceId: null };
  const orgId = typeof claims.orgId === 'string' ? claims.orgId : null;
  const organizationName =
    typeof claims.organizationName === 'string'
      ? claims.organizationName
      : typeof claims.orgName === 'string'
        ? claims.orgName
        : null;
  const workspaceId = typeof claims.workspaceId === 'string' ? claims.workspaceId : null;
  return { orgId, organizationName, workspaceId };
}

interface OrgSessionState {
  /** Display-only hints (from verified session token claims / server response). */
  activeOrgId: string | null;
  activeOrgName: string | null;
  activeWorkspaceId: string | null;
  activeWorkspaceName: string | null;
  /** True while a /auth/sessions/org request (switch/establish) is in flight. */
  switching: boolean;
  error: string | null;
}

const bootstrap = readOrgFromAccessToken(getToken());

const initialState: OrgSessionState = {
  // Bootstrap the display hints from the already-verified session token
  // (memory), purely for the header labels. No org credential is persisted
  // anywhere separately.
  activeOrgId: bootstrap.orgId,
  activeOrgName: bootstrap.organizationName,
  activeWorkspaceId: bootstrap.workspaceId,
  activeWorkspaceName: null,
  switching: false,
  error: null,
};

/** The server-echoed scope of a successful switch (display metadata only). */
export interface SessionScopeResult {
  orgId: string;
  organizationName: string | null;
  workspaceId: string | null;
  workspaceName: string | null;
}

async function getJson<T>(url: string, params?: Record<string, unknown>): Promise<T | null> {
  // Errors PROPAGATE — each caller decides its own fallback: establishOrgScope
  // must distinguish "org list unreachable" (reject → sign-in proceeds
  // unscoped, manual retry via the header) from "no organizations" (no-op),
  // and the cascade's workspace lookup degrades to org-only scope.
  const { data } = await apiClient.get<T>(url, { params });
  return data ?? null;
}

/** Workspaces visible to the CURRENT (org-scoped) session. */
async function listWorkspaces(): Promise<WorkspaceLite[]> {
  const data = await getJson<{ data?: WorkspaceLite[] }>('/organizations/v1/directory/workspaces', {
    limit: 100,
  });
  return data?.data ?? [];
}

/**
 * Convenience cascade: the remembered workspace (if it still exists in this
 * org) wins; else exactly-one-workspace auto-picks; else null (the user is
 * asked via the WorkspaceSelector).
 */
function pickWorkspace(list: WorkspaceLite[]): WorkspaceLite | null {
  const remembered = localStorage.getItem(LAST_WORKSPACE_KEY);
  if (remembered) {
    const match = list.find((w) => w.id === remembered);
    if (match) return match;
  }
  if (list.length === 1) return list[0];
  return null;
}

/** familyId claim of a refresh token (display/comparison only — the server
 *  verifies ownership itself; malformed → null). */
function familyIdOf(refreshToken: string): string | null {
  const claims = decodeJwtClaims(refreshToken);
  return typeof claims?.familyId === 'string' ? claims.familyId : null;
}

/**
 * Adopt a freshly-minted org-session pair and best-effort revoke the
 * org-session family it replaces (`prevOrgRefresh` — the old family's
 * refresh token, captured BEFORE adoption; null when the previous session
 * was not org-scoped, in which case the base login family legitimately
 * survives: org switches never implicitly revoke non-org families). The
 * server only accepts tokens belonging to the caller's own org-session
 * families; errors are swallowed (hygiene, never a hard requirement).
 */
async function adoptAndCleanup(
  res: OrgSessionResponse,
  prevOrgRefresh: string | null,
): Promise<void> {
  adoptSession({ accessToken: res.accessToken, refreshToken: res.refreshToken });
  if (!prevOrgRefresh) return;
  const prevFamily = familyIdOf(prevOrgRefresh);
  const newFamily = familyIdOf(res.refreshToken);
  if (!prevFamily || prevFamily === newFamily) return;
  try {
    await logoutOrgSessionFamily(prevOrgRefresh);
  } catch {
    // Best-effort hygiene; the server re-authorizes every family anyway.
  }
}

/**
 * The full atomic org switch + workspace cascade:
 * 1. POST /auth/sessions/org {orgId} — server-side authorization gate.
 * 2. On success ONLY: adopt the new pair atomically + revoke the org family
 *    it replaced (the base login family always survives).
 * 3. Under the new org scope, resolve a workspace (remembered → single →
 *    none). If one is chosen, a SECOND scoped switch org+workspace mints the
 *    `workspaceId` claim (server verifies membership first) and revokes the
 *    org-only family from step 2.
 * 4. Reset the RTK Query cache so no stale or in-flight data from the
 *    previous scope survives.
 * On rejection of step 1: nothing changes (old tokens/cache/UI persist).
 * If step 3's second switch fails, the org-only session from step 2 is kept.
 */
async function switchWithWorkspaceCascade(
  orgId: string,
  orgNameHint?: string,
): Promise<SessionScopeResult> {
  // Snapshot the previous family BEFORE any adoption: once the new pair is
  // stored, getRefreshToken() returns the NEW token and the old family would
  // be unrevocable (revoking the new token would orphan the current session).
  const prevClaims = decodeJwtClaims(getToken());
  const prevOrgRefresh = typeof prevClaims?.orgId === 'string' ? getRefreshToken() : null;

  const res = await switchOrgSession(orgId);
  await adoptAndCleanup(res, prevOrgRefresh);
  localStorage.setItem(LAST_ORG_KEY, orgId);

  // Resolve display names (best-effort; the labels are hints only).
  let organizationName: string | null =
    typeof res.organizationName === 'string' && res.organizationName.length > 0
      ? res.organizationName
      : (orgNameHint ?? null);
  if (!organizationName) {
    const orgs = await getJson<{ data?: OrgLite[] }>('/organizations');
    organizationName = orgs?.data?.find((o) => o._id === orgId)?.name ?? null;
  }

  // Workspace cascade under the new org scope.
  let workspaceId: string | null = null;
  let workspaceName: string | null = null;
  try {
    const chosen = pickWorkspace(await listWorkspaces());
    if (chosen) {
      const res2 = await switchOrgSession(orgId, chosen.id);
      await adoptAndCleanup(res2, res.refreshToken);
      localStorage.setItem(LAST_WORKSPACE_KEY, chosen.id);
      workspaceId = res2.workspaceId ?? chosen.id;
      workspaceName = chosen.name ?? null;
    }
  } catch {
    // Keep the org-only scope already adopted — the WorkspaceSelector can
    // retry manually; scoping must never hard-fail the whole switch.
  }

  return { orgId, organizationName, workspaceId, workspaceName };
}

export const switchOrganization = createAsyncThunk<SessionScopeResult, string>(
  'orgSession/switch',
  async (orgId, { dispatch, rejectWithValue }) => {
    try {
      // Old session stays live until the success path adopts the new pair;
      // pending/rejected is handled in the slice (switching flag + error
      // message, session untouched on failure).
      const result = await switchWithWorkspaceCascade(orgId);
      dispatch(apiSlice.util.resetApiState());
      return result;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Organization switch was rejected.';
      return rejectWithValue(message);
    }
  },
);

/**
 * Narrow the CURRENT org scope to a workspace the user picked manually (the
 * >1-workspace branch of the cascade). Server verifies membership before
 * minting the claim; a rejection leaves the current session untouched.
 */
export const switchWorkspace = createAsyncThunk<
  SessionScopeResult,
  { workspaceId: string; workspaceName?: string }
>(
  'orgSession/switchWorkspace',
  async ({ workspaceId, workspaceName }, { dispatch, rejectWithValue }) => {
    const current = readOrgFromAccessToken(getToken());
    if (!current.orgId) {
      return rejectWithValue('Select an organization first.');
    }
    try {
      // Capture the family being replaced BEFORE adoption (same reason as the
      // cascade helper — after adoptSession the old token is unrecoverable).
      const prevOrgRefresh = typeof current.orgId === 'string' ? getRefreshToken() : null;

      const res = await switchOrgSession(current.orgId, workspaceId);
      await adoptAndCleanup(res, prevOrgRefresh);
      localStorage.setItem(LAST_WORKSPACE_KEY, workspaceId);
      dispatch(apiSlice.util.resetApiState());
      return {
        orgId: current.orgId,
        organizationName: current.organizationName,
        workspaceId: res.workspaceId ?? workspaceId,
        workspaceName: workspaceName ?? null,
      };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Workspace switch was rejected.';
      return rejectWithValue(message);
    }
  },
);

/**
 * Establish the full org (+workspace) scope for a freshly-issued login
 * session.
 *
 * A `POST /auth/login` token carries no `orgId` claim, and every org-scoped
 * service rejects such a session with 403 ("Organization tenant context is
 * required" / "Verified organization identity required for policy resource").
 * The scope is only granted by `POST /auth/sessions/org`, so after login the
 * client must exchange the default-scoped pair for a scoped one, then narrow
 * it to a workspace via the cascade above.
 *
 * Resolution order: the last org this browser switched to (if still
 * accessible), else the first org the caller can read. No-op when the token
 * already carries BOTH org and workspace scope, or when the caller has no
 * organizations at all — in that case the user lands on the app as before
 * and can pick manually. Never blocks sign-in: failures are silent here
 * (the header selectors remain the manual retry path).
 */
export const establishOrgScope = createAsyncThunk<SessionScopeResult | null, void>(
  'orgSession/establish',
  async (_, { dispatch, rejectWithValue }) => {
    const current = readOrgFromAccessToken(getToken());
    if (current.orgId && current.workspaceId) return null; // fully scoped — no-op

    // Self-heal a session that ALREADY has org scope but no workspace hint:
    // run only the workspace cascade (no redundant org re-switch — the token
    // already authenticates the scope; a re-switch would mint a fresh family
    // on every page load for orgs with multiple workspaces).
    if (current.orgId) {
      try {
        const chosen = pickWorkspace(await listWorkspaces());
        if (!chosen) return null; // >1 workspaces & none remembered — UI asks.
        const prevOrgRefresh = getRefreshToken();
        const res = await switchOrgSession(current.orgId, chosen.id);
        await adoptAndCleanup(res, prevOrgRefresh);
        localStorage.setItem(LAST_WORKSPACE_KEY, chosen.id);
        dispatch(apiSlice.util.resetApiState());
        return {
          orgId: current.orgId,
          organizationName: current.organizationName,
          workspaceId: res.workspaceId ?? chosen.id,
          workspaceName: chosen.name ?? null,
        };
      } catch (err: unknown) {
        return rejectWithValue(err instanceof Error ? err.message : 'Could not scope workspace.');
      }
    }

    let orgId = current.orgId;
    if (!orgId) {
      const data = await getJson<{ data?: OrgLite[] }>('/organizations');
      const orgs = data?.data ?? [];
      if (orgs.length === 0) return null; // no orgs — nothing to scope

      const remembered = localStorage.getItem(LAST_ORG_KEY);
      const target = orgs.find((o) => o._id === remembered) ?? orgs[0];
      orgId = target._id;
    }

    try {
      const result = await switchWithWorkspaceCascade(orgId, current.organizationName ?? undefined);
      // Same cache-wipe contract as a manual switch: nothing from the
      // unscoped pre-login session may survive the scope change.
      dispatch(apiSlice.util.resetApiState());
      return result;
    } catch (err: unknown) {
      // Never block sign-in on scoping: the user stays authenticated with
      // the default session and can retry from the header selectors.
      return rejectWithValue(err instanceof Error ? err.message : 'Could not scope organization.');
    }
  },
);

function applyScope(
  state: OrgSessionState,
  payload: SessionScopeResult | null,
  syncFromToken: boolean,
): void {
  state.switching = false;
  state.error = null;
  if (payload) {
    state.activeOrgId = payload.orgId;
    state.activeOrgName = payload.organizationName;
    state.activeWorkspaceId = payload.workspaceId;
    state.activeWorkspaceName = payload.workspaceName;
  } else if (syncFromToken) {
    const fromToken = readOrgFromAccessToken(getToken());
    state.activeOrgId = fromToken.orgId;
    state.activeOrgName = fromToken.organizationName;
    state.activeWorkspaceId = fromToken.workspaceId;
    // A workspace name is display-only metadata the server never signs;
    // after a token-sync (login/bootstrap) there is no local source for it.
    state.activeWorkspaceName = fromToken.workspaceId ? state.activeWorkspaceName : null;
  }
}

const orgSessionSlice = createSlice({
  name: 'orgSession',
  initialState,
  reducers: {
    /** Re-read the display hints after a login/re-login established new tokens. */
    syncOrgFromSession(state) {
      applyScope(state, null, true);
    },
    clearOrgError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(switchOrganization.pending, (state) => {
        state.switching = true;
        state.error = null;
      })
      .addCase(switchOrganization.fulfilled, (state, action) => {
        applyScope(state, action.payload, false);
        // Same-org re-switch with no workspace found: clear stale workspace
        // hints so the label reflects the actual (org-only) scope.
        if (action.payload.workspaceId === null) {
          state.activeWorkspaceId = null;
          state.activeWorkspaceName = null;
        }
      })
      .addCase(switchOrganization.rejected, (state, action) => {
        state.switching = false;
        // Previous session and its display hints are intentionally untouched.
        state.error = (action.payload as string) || 'Organization switch failed.';
      })
      .addCase(switchWorkspace.pending, (state) => {
        state.switching = true;
        state.error = null;
      })
      .addCase(switchWorkspace.fulfilled, (state, action) => {
        applyScope(state, action.payload, false);
      })
      .addCase(switchWorkspace.rejected, (state, action) => {
        state.switching = false;
        state.error = (action.payload as string) || 'Workspace switch failed.';
      })
      // Automatic post-login scoping (see establishOrgScope). Silent: it must
      // not surface an error banner on the dashboard when it no-ops or fails —
      // the header selectors remain the manual retry path.
      .addCase(establishOrgScope.pending, (state) => {
        state.switching = true;
      })
      .addCase(establishOrgScope.fulfilled, (state, action) => {
        applyScope(state, action.payload, true);
      })
      .addCase(establishOrgScope.rejected, (state) => {
        state.switching = false;
        state.error = null;
      })
      // Session established through any path (legacy login, or MFA → LoginPage
      // dispatches sessionEstablished after adoptSession persisted the pair):
      // refresh the display hints from the just-issued verified token. Tokens
      // are already in storage synchronously when these actions are dispatched.
      .addCase(loginUser.fulfilled, (state) => {
        applyScope(state, null, true);
      })
      .addCase(sessionEstablished, (state) => {
        applyScope(state, null, true);
      })
      // Signing out (or resetting auth) ends the org session too — the hints
      // go away with the token; nothing org-related persists.
      .addCase(logoutUser.fulfilled, (state) => {
        state.activeOrgId = null;
        state.activeOrgName = null;
        state.activeWorkspaceId = null;
        state.activeWorkspaceName = null;
        state.switching = false;
        state.error = null;
      })
      .addCase(resetAuth, (state) => {
        state.activeOrgId = null;
        state.activeOrgName = null;
        state.activeWorkspaceId = null;
        state.activeWorkspaceName = null;
        state.switching = false;
        state.error = null;
      });
  },
});

export const { syncOrgFromSession, clearOrgError } = orgSessionSlice.actions;
export default orgSessionSlice.reducer;
